import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField
} from '@mui/material'
import { SelectInput } from 'components'
import { t } from 'i18n'
import React, { useState } from 'react'
import { toast } from 'react-toastify'

interface CreateHeatmapDialogProps {
  open: boolean
  onClose: () => void
  onSuccess: (params: {
    name: string
    season: string
    file: File
  }) => Promise<void>
}

const CreateHeatmapDialog: React.FC<CreateHeatmapDialogProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [name, setName] = useState('')
  const [season, setSeason] = useState('winter')
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)

  const seasonOptions = [
    { id: 'winter', label: t('winter') || 'Winter' },
    { id: 'summer', label: t('summer') || 'Summer' }
  ]

  const handleClose = () => {
    if (!uploading) {
      setName('')
      setSeason('winter')
      setFile(null)
      setUploading(false)
      onClose()
    }
  }

  const handleConfirm = async () => {
    if (!name.trim() || !file || !season || uploading) return

    try {
      setUploading(true)
      await onSuccess({
        name: name.trim(),
        season: season.toLowerCase(),
        file
      })
      handleClose()
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message || e?.message || 'Failed to upload heatmap'
      )
    } finally {
      setUploading(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('heatmaps.add') || 'Add Heatmap'}</DialogTitle>
      <DialogContent>
        <Box display="flex" gap={2} flexDirection="column" mt={1}>
          <TextField
            label={t('name') || 'Name'}
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
          />
          <SelectInput
            name="season"
            label={t('season') || 'Season'}
            value={season}
            onChange={(e: any) => setSeason(e.target.value)}
            options={seasonOptions}
            translateText={false}
          />
          <Button variant="outlined" component="label">
            {file ? file.name : t('heatmaps.chooseFile') || 'Choose file'}
            <input
              hidden
              type="file"
              accept="image/*"
              onChange={e => {
                const selectedFile = e.target.files?.[0] || null
                setFile(selectedFile)
              }}
            />
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          {t('cancel') || 'Cancel'}
        </Button>
        <Button
          variant="contained"
          disabled={!name.trim() || !file || !season || uploading}
          onClick={handleConfirm}
        >
          {uploading
            ? t('heatmaps.uploading') || 'Uploading...'
            : t('save') || 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CreateHeatmapDialog
